import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Admin EVII chat shell state.
 *
 * Two concerns:
 *   1. `view` — the presentation state shared by the header launcher, the chat
 *      shell, and the AppShell reflow (closed / compact popup / docked side
 *      panel). In-memory, so EVII opens closed on every load.
 *   2. `inquiriesByUser` — the recent-inquiry memory. Capped at the last
 *      MAX_RECENT_INQUIRIES, PER USER, and persisted to localStorage. Keyed by
 *      user id so a shared device never leaks one operator's inquiries to
 *      another (each id has its own slice; the chat reads only the current
 *      user's). Same device-pref persist pattern the rest of the app uses.
 *
 * This store holds NO data access — the chat itself only calls the proven,
 * server-scoped /admin-assistant endpoint. This is presentation state only.
 */

export type EvieView = 'closed' | 'popup' | 'panel';

export interface EvieInquiry {
  id: string;
  question: string;
  answer: string;
  at: number;
}

/** Warren's cap: keep only the last few inquiries to bound memory use. */
export const MAX_RECENT_INQUIRIES = 3;

/** Per-operator EVII voice choice. Operator-facing as female/male; maps
 *  server-side to the Polly voices (female=Tiffany, male=Matthew). Defaults to
 *  female -- the map only holds explicit choices, read with a `?? 'female'`. */
export type EvieVoiceChoice = 'female' | 'male';

interface EvieState {
  view: EvieView;
  open: () => void;
  close: () => void;
  /** Header-icon click: open the popup from closed, or close from any open state. */
  toggle: () => void;
  /** Popup -> docked side panel. */
  maximize: () => void;
  /** Docked side panel -> compact popup. */
  minimize: () => void;

  /** Voice mode (hands-free listen -> send -> speak loop) is live. In-memory
   *  only, like `view` -- never persisted, so a reload always starts voice off.
   *  Drives the launcher glow and the chat's voice loop; the chat owns the loop,
   *  the launcher only reads this for the glow. */
  voiceActive: boolean;
  setVoiceActive: (active: boolean) => void;
  toggleVoice: () => void;

  inquiriesByUser: Record<string, EvieInquiry[]>;
  /** Prepend an inquiry for `userId`, capped to MAX_RECENT_INQUIRIES. No-op
   *  without a userId (never write to a shared/unkeyed bucket). */
  recordInquiry: (userId: string | null | undefined, inquiry: EvieInquiry) => void;

  /** Per-operator EVII voice (female | male), persisted + keyed by user like
   *  inquiriesByUser so each admin's choice sticks for them. No-op without a
   *  userId. Read with a `?? 'female'` default. */
  voiceByUser: Record<string, EvieVoiceChoice>;
  setVoiceChoice: (userId: string | null | undefined, choice: EvieVoiceChoice) => void;

  /** Whether the mic auto-starts when the EVII chat opens. Per-user, persisted
   *  like voiceByUser. Default ON (read with `?? true`); off waits for a tap. */
  micAutostartByUser: Record<string, boolean>;
  setMicAutostart: (userId: string | null | undefined, on: boolean) => void;
}

export const useEvieStore = create<EvieState>()(
  persist(
    (set) => ({
      view: 'closed',
      open: () => set({ view: 'popup' }),
      close: () => set({ view: 'closed' }),
      toggle: () => set((s) => ({ view: s.view === 'closed' ? 'popup' : 'closed' })),
      maximize: () => set({ view: 'panel' }),
      minimize: () => set({ view: 'popup' }),

      voiceActive: false,
      setVoiceActive: (active) => set({ voiceActive: active }),
      toggleVoice: () => set((s) => ({ voiceActive: !s.voiceActive })),

      inquiriesByUser: {},
      recordInquiry: (userId, inquiry) =>
        set((s) => {
          if (!userId) return {};
          const existing = s.inquiriesByUser[userId] ?? [];
          const next = [inquiry, ...existing].slice(0, MAX_RECENT_INQUIRIES);
          return { inquiriesByUser: { ...s.inquiriesByUser, [userId]: next } };
        }),

      voiceByUser: {},
      setVoiceChoice: (userId, choice) =>
        set((s) => {
          if (!userId) return {};
          return { voiceByUser: { ...s.voiceByUser, [userId]: choice } };
        }),

      micAutostartByUser: {},
      setMicAutostart: (userId, on) =>
        set((s) => {
          if (!userId) return {};
          return { micAutostartByUser: { ...s.micAutostartByUser, [userId]: on } };
        }),
    }),
    {
      name: 'evi-admin-evie',
      // Persist the per-user memory + mic auto-start pref; `view` stays
      // in-memory so EVII is closed on load.
      partialize: (s) => ({
        inquiriesByUser: s.inquiriesByUser,
        voiceByUser: s.voiceByUser,
        micAutostartByUser: s.micAutostartByUser,
      }),
    },
  ),
);
